export type OrderMappingTag = { field: string; value: string };

export type OrderMappingFinishCandidate = {
  material_id: string;
  material_code: string;
  material_name: string;
  finish_code: string | null;
};

export type OrderMappingFinishMatch = {
  material_id: string | null;
  reason: string;
};

export type OrderMappingBulkPlan = {
  material_use_id: string;
  use_code: string;
  material_id: string;
  gap_keys: string[];
};

const FIELD_LABEL: Record<string, string> = {
  width: "Width",
  length: "Length",
  series: "Series",
  roof_style: "Roof style",
  package: "Package",
  roof_type: "Roof type",
  roof_color: "Roof color",
  siding_type: "Siding type",
  siding_color: "Siding color",
  trim_color: "Trim color",
  addon_name: "Add-on",
};

export function orderMappingTagKey(tag: OrderMappingTag): string {
  return `${tag.field}:${tag.value}`;
}

export function orderMappingTagLabel(tag: OrderMappingTag): string {
  return `${FIELD_LABEL[tag.field] ?? tag.field} ${tag.value}`;
}

export function collectOrderMappingTags(gaps: readonly { aspects: readonly OrderMappingTag[] }[]): OrderMappingTag[] {
  const seen = new Map<string, OrderMappingTag>();
  for (const gap of gaps) {
    for (const aspect of gap.aspects) {
      const field = aspect.field.trim();
      const value = aspect.value.trim();
      if (!field || !value) continue;
      const tag = { field, value };
      const key = orderMappingTagKey(tag);
      if (!seen.has(key)) seen.set(key, tag);
    }
  }
  return [...seen.values()].sort((a, b) => orderMappingTagLabel(a).localeCompare(orderMappingTagLabel(b)));
}

export function gapHasOrderMappingTags(gap: { aspects: readonly OrderMappingTag[] }, tags: readonly OrderMappingTag[]): boolean {
  return tags.every((tag) => gap.aspects.some((aspect) => aspect.field === tag.field && aspect.value === tag.value));
}

function tokens(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function candidateTokens(member: OrderMappingFinishCandidate): Set<string> {
  return new Set(tokens([member.finish_code ?? "", member.material_name, member.material_code].join(" ")));
}

/**
 * Words from the match tag are tested against each finish.
 * A word on every finish, or on none, does not decide.
 * A word on exactly one finish selects that finish.
 * Two such words must agree.
 */
export function matchFinishByTag(
  tag: OrderMappingTag,
  members: readonly OrderMappingFinishCandidate[],
): OrderMappingFinishMatch {
  const unique = new Map<string, OrderMappingFinishCandidate>();
  for (const member of members) {
    if (!member.material_id || unique.has(member.material_id)) continue;
    unique.set(member.material_id, member);
  }
  const candidates = [...unique.values()];
  if (candidates.length === 0) return { material_id: null, reason: "No member SKUs on this part use." };

  const words = [...new Set(tokens(`${tag.field.replace(/_/g, " ")} ${tag.value}`))];
  if (words.length === 0) return { material_id: null, reason: "That tag has no words to match." };

  const rows = candidates.map((member) => ({ member, tokens: candidateTokens(member) }));
  const distinctive: Array<{ word: string; material_id: string }> = [];
  for (const word of words) {
    const hits = rows.filter((row) => row.tokens.has(word));
    if (hits.length === 1) distinctive.push({ word, material_id: hits[0]!.member.material_id });
  }
  const chosen = [...new Set(distinctive.map((hit) => hit.material_id))];
  if (chosen.length === 1) {
    const used = distinctive.filter((hit) => hit.material_id === chosen[0]).map((hit) => hit.word);
    return { material_id: chosen[0]!, reason: `${used.join(" and ")} picks one finish.` };
  }
  if (chosen.length > 1) return { material_id: null, reason: "Those words point at more than one finish." };
  return { material_id: null, reason: "No word on that tag picks a single finish." };
}

/** Each line uses the tag assigned to its channel. A channel with no tag is left unmatched. */
export function matchOrderMappingByChannel(input: {
  gaps: ReadonlyArray<{ key: string; channel: string; members: readonly OrderMappingFinishCandidate[] }>;
  tags: readonly OrderMappingTag[];
  tagKeyByChannel: Readonly<Record<string, string>>;
}): Record<string, OrderMappingFinishMatch> {
  const out: Record<string, OrderMappingFinishMatch> = {};
  for (const gap of input.gaps) {
    const tagKey = input.tagKeyByChannel[gap.channel] ?? "";
    const tag = input.tags.find((item) => orderMappingTagKey(item) === tagKey);
    if (!tag) {
      out[gap.key] = {
        material_id: null,
        reason: gap.channel ? `No match tag for the ${gap.channel} channel.` : "No match tag for parts with no channel.",
      };
      continue;
    }
    out[gap.key] = matchFinishByTag(tag, gap.members);
  }
  return out;
}

export type OrderMappingSkuRule = {
  material_use_id: string;
  material_id: string;
  priority: number;
  indicators: ReadonlyArray<{ field: string; op: "equals" | "contains"; value: string }>;
};

function indicatorHitsGap(
  gap: { aspects: readonly OrderMappingTag[] },
  indicator: OrderMappingSkuRule["indicators"][number],
): boolean {
  if (indicator.op === "contains") {
    const needle = indicator.value.trim().toLowerCase();
    return gap.aspects.some(
      (aspect) => aspect.field === indicator.field && aspect.value.toLowerCase().includes(needle),
    );
  }
  const actual = gap.aspects.find((aspect) => aspect.field === indicator.field)?.value ?? "";
  return actual.trim().toLowerCase() === indicator.value.trim().toLowerCase();
}

/** The one SKU a saved rule assigns this row, when the match is unique. */
export function skuRuleMaterialForGap(
  gap: { material_use_id: string; aspects: readonly OrderMappingTag[] },
  rules: readonly OrderMappingSkuRule[],
): string | null {
  const matching = rules.filter(
    (rule) => rule.material_use_id === gap.material_use_id && rule.indicators.every((indicator) => indicatorHitsGap(gap, indicator)),
  );
  if (matching.length === 0) return null;
  const priority = Math.min(...matching.map((rule) => rule.priority));
  const atPriority = matching.filter((rule) => rule.priority === priority);
  const specific = Math.max(...atPriority.map((rule) => rule.indicators.length));
  const winners = atPriority.filter((rule) => rule.indicators.length === specific);
  const materials = [...new Set(winners.map((rule) => rule.material_id))];
  return materials.length === 1 ? materials[0]! : null;
}

export function planOrderMappingBulkRules(
  gaps: readonly { key: string; material_use_id: string; use_code: string }[],
  materialByGap: Readonly<Record<string, string>>,
): { plans: OrderMappingBulkPlan[]; skipped: string[] } {
  const byUse = new Map<string, { use_code: string; keys: string[]; materials: Set<string> }>();
  for (const gap of gaps) {
    const bucket = byUse.get(gap.material_use_id) ?? { use_code: gap.use_code, keys: [], materials: new Set<string>() };
    bucket.keys.push(gap.key);
    const materialId = materialByGap[gap.key];
    if (materialId) bucket.materials.add(materialId);
    byUse.set(gap.material_use_id, bucket);
  }
  const plans: OrderMappingBulkPlan[] = [];
  const skipped: string[] = [];
  for (const [material_use_id, bucket] of byUse) {
    if (bucket.materials.size === 0) continue;
    if (bucket.materials.size > 1) {
      skipped.push(bucket.use_code);
      continue;
    }
    plans.push({
      material_use_id,
      use_code: bucket.use_code,
      material_id: [...bucket.materials][0]!,
      gap_keys: bucket.keys,
    });
  }
  plans.sort((a, b) => a.use_code.localeCompare(b.use_code));
  return { plans, skipped };
}
